import React, { ReactElement, useMemo, useState } from 'react';

import { orderBy } from 'lodash';

import {
  ListTable,
  Badge,
  RemoveRedEyeOutlinedIcon,
  DeleteOutlinedIcon,
  SendMessageOutlinedIcon,
} from 'elmo-elements';

import { createFormattedDateString } from '@/lib/util';

import { ReviewerType, ReviewerStatus } from '@/modules/types/reviewers';

interface ReviewersListViewProps {
  reviewers: ReviewerType[];
  onViewComments: (reviewerIndex: number, reviewers: { id: string }[]) => void;
  onDeleteReviewer: (reviewerId: string) => void;
  onSendReminder: (reviewerId: string) => void;
}

interface ReviewersListBadgeProps {
  status: number;
}
const ReviewersListBadge = ({
  status,
}: ReviewersListBadgeProps): ReactElement => {
  const badgeData: {
    label: string;
    theme: 'grey' | 'outlined' | 'warning' | 'info' | 'success' | 'danger';
  } = {
    label: 'Not Started',
    theme: 'outlined',
  };
  switch (status) {
    case 1:
      badgeData.label = 'In Progress';
      badgeData.theme = 'warning';
      break;
    case 2:
      badgeData.label = 'Complete';
      badgeData.theme = 'success';
      break;
    default:
  }
  return <Badge type={badgeData.theme}>{badgeData.label}</Badge>;
};

const tableColumns = ['reviewerName', 'email', 'lastModified'];

const ReviewersListView = ({
  reviewers,
  onViewComments,
  onDeleteReviewer,
  onSendReminder,
}: ReviewersListViewProps): ReactElement => {
  const [sortIndex, setSortIndex] = useState(2);
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');

  const sortedReviewers = useMemo(
    () =>
      orderBy(
        reviewers.map((reviewer) => ({
          id: reviewer.id,
          reviewerName: `${reviewer.reviewerMetadata.firstName} ${reviewer.reviewerMetadata.lastName}`,
          email: reviewer.reviewerMetadata.email,
          lastModified:
            reviewer.modifiedAt &&
            createFormattedDateString(reviewer.modifiedAt),
          status: <ReviewersListBadge status={reviewer.status} />,
          statusValue: reviewer.status,
        })),
        [tableColumns[sortIndex]],
        [sortDirection],
      ),
    [reviewers, sortIndex, sortDirection],
  );

  const renderActions = (data: any, index: number) => [
    {
      icon: <RemoveRedEyeOutlinedIcon />,
      label: 'View comments',
      onClick: (actionIndex: number) => {
        onViewComments(actionIndex, sortedReviewers);
      },
    },
    ...(sortedReviewers[index].statusValue !== ReviewerStatus.COMPLETED
      ? [
          {
            icon: <SendMessageOutlinedIcon />,
            label: 'Send reminder',
            onClick: (actionIndex: number) => {
              onSendReminder(sortedReviewers[actionIndex].id);
            },
            withDivider:
              sortedReviewers[index].statusValue === ReviewerStatus.NOT_STARTED,
          },
        ]
      : []),
    ...(sortedReviewers[index].statusValue === ReviewerStatus.NOT_STARTED
      ? [
          {
            icon: <DeleteOutlinedIcon />,
            label: 'Remove',
            onClick: (actionIndex: number) => {
              onDeleteReviewer(sortedReviewers[actionIndex].id);
            },
          },
        ]
      : []),
  ];

  const handleSort = (index: any, sort: any) => {
    setSortIndex(index);
    setSortDirection(sort);
  };

  return (
    <ListTable
      data={sortedReviewers}
      actions={renderActions}
      onSort={handleSort}
      sortColumn={sortIndex}
      sortDirection={sortDirection}
      id="ReviewersList"
    >
      <ListTable.Column label="Name" property="reviewerName" sortable={true} />
      <ListTable.Column label="Email" property="email" sortable={true} />
      <ListTable.Column
        label="Last Modified"
        property="lastModified"
        sortable={true}
      />
      <ListTable.Column label="Status" property="status" />
    </ListTable>
  );
};

export default ReviewersListView;
